import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardCheck,
  CloudSun,
  Compass,
  ExternalLink,
  FileCheck2,
  FileText,
  Gauge,
  Loader2,
  LockKeyhole,
  Plane,
  RadioTower,
  Save,
  ShieldCheck,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import {
  formatServiceNumber,
  useTrainingSession,
} from "../../context/TrainingSessionContext";

const PDF_PATH =
  "/training-assets/leading-cadet-notebook.pdf";

const field = (
  key,
  label,
  options = {}
) => ({
  key,
  label,
  multiline:
    options.multiline ?? true,
  rows: options.rows ?? 3,
  placeholder:
    options.placeholder ?? "",
});

const listFields = (
  prefix,
  count,
  label = "Answer"
) =>
  Array.from(
    { length: count },
    (_, index) =>
      field(
        `${prefix}${index + 1}`,
        `${label} ${index + 1}`,
        {
          multiline:
            false,
        }
      )
  );

const groupedFields = (
  groups
) =>
  groups.flatMap(
    ({
      key,
      label,
      items,
    }) =>
      items.map(
        (item, index) =>
          field(
            `${key}${index + 1}`,
            `${label} – ${item}`
          )
      )
  );

export const LEADING_SECTIONS = [
  {
    id: "airmanship",
    title:
      "Airmanship Knowledge",
    icon: RadioTower,
    pages: "4–17",
    description:
      "Air traffic control, Rules of the Air and military aircraft designations.",
  },
  {
    id: "flight",
    title:
      "Principles of Flight",
    icon: Plane,
    pages: "18–31",
    description:
      "Lift, stability, stalls, controls, gliders and rotary-wing aircraft.",
  },
  {
    id: "navigation",
    title:
      "Basic Navigation Using Map and Compass",
    icon: Compass,
    pages: "32–45",
    description:
      "North, compass work, bearings, Naismith's Rule, navigation techniques and weather.",
  },
];

export const LEADING_TASKS = [
  // =======================================================================
  // SECTION 1 — AIRMANSHIP KNOWLEDGE
  // =======================================================================
  {
    id: "airmanship-1",
    section:
      "airmanship",
    code: "P1",
    type: "know",
    mandatory: true,
    page: 6,
    title:
      "Types of Air Traffic Controllers",
    prompt:
      "Describe the roles of the aerodrome, approach and runway controllers.",
    fields: [
      field(
        "aerodrome",
        "Aerodrome Controller",
        { rows: 4 }
      ),
      field(
        "approach",
        "Approach Controller",
        { rows: 4 }
      ),
      field(
        "runway",
        "Runway Controller",
        { rows: 4 }
      ),
    ],
  },
  {
    id: "airmanship-2",
    section:
      "airmanship",
    code: "P2",
    type: "know",
    mandatory: true,
    page: 6,
    title:
      "ATC Active Communication",
    prompt:
      "Describe the two active communication methods shown in the notebook.",
    fields: [
      field(
        "telephone",
        "Telephone"
      ),
      field(
        "radio",
        "Radio (VHF and UHF)"
      ),
    ],
  },
  {
    id: "airmanship-3",
    section:
      "airmanship",
    code: "P2",
    type: "know",
    mandatory: true,
    page: 7,
    title:
      "Passive Signalling",
    prompt:
      "Name and describe the four runway markers pictured in the source notebook.",
    fields: groupedFields([
      {
        key: "markerName",
        label: "Name",
        items: [
          "Marker 1",
          "Marker 2",
          "Marker 3",
          "Marker 4",
        ],
      },
      {
        key: "markerDescription",
        label: "Description",
        items: [
          "Marker 1",
          "Marker 2",
          "Marker 3",
          "Marker 4",
        ],
      },
    ]),
  },
  {
    id: "airmanship-4",
    section:
      "airmanship",
    code: "P3",
    type: "know",
    mandatory: true,
    page: 7,
    title: "Radio Aids",
    prompt:
      "Describe Digital Resolution Direction Finding (DRDF) and the Instrument Landing System (ILS).",
    fields: [
      field(
        "drdf",
        "Digital Resolution Direction Finding (DRDF)"
      ),
      field(
        "ils",
        "Instrument Landing System (ILS)"
      ),
    ],
  },
  {
    id: "airmanship-5",
    section:
      "airmanship",
    code: "P3",
    type: "know",
    mandatory: true,
    page: 8,
    title: "Radar Aids",
    prompt:
      "Describe Surveillance Radar and Precision Approach Radar.",
    fields: [
      field(
        "surveillance",
        "Surveillance Radar"
      ),
      field(
        "precision",
        "Precision Approach Radar"
      ),
    ],
  },
  {
    id: "airmanship-6",
    section:
      "airmanship",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 8,
    title:
      "Handing Responsibility Between Controllers",
    prompt:
      "Explain what each person must do to enable a Typhoon to depart RAF Coningsby and continue to a low-level sortie in North Wales.",
    fields: groupedFields([
      {
        key: "role",
        label: "Role",
        items: [
          "Pilot",
          "Aerodrome Controller",
          "Approach Controller",
          "Next Airspace Controller",
        ],
      },
    ]),
  },
  {
    id: "airmanship-7",
    section:
      "airmanship",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 9,
    title:
      "ATC Communication Comparison",
    prompt:
      "Compare the advantages and limitations of the four communication methods in the notebook.",
    fields: groupedFields([
      {
        key: "advantages",
        label: "Advantages",
        items: [
          "Telephone (Defence Fixed Telecoms System)",
          "Tele-talk",
          "Radio (VHF and UHF)",
          "Passive Signalling",
        ],
      },
      {
        key: "limitations",
        label: "Limitations",
        items: [
          "Telephone (Defence Fixed Telecoms System)",
          "Tele-talk",
          "Radio (VHF and UHF)",
          "Passive Signalling",
        ],
      },
    ]),
  },
  {
    id: "airmanship-8",
    section:
      "airmanship",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 10,
    title:
      "Importance of Effective Communication",
    prompt:
      "Justify the importance of effective communication around an airfield and include examples.",
    fields: [
      field(
        "answer",
        "Your answer",
        { rows: 9 }
      ),
    ],
  },
  {
    id: "airmanship-9",
    section:
      "airmanship",
    code: "P4",
    type: "know",
    mandatory: true,
    page: 11,
    title:
      "Classes of Aircraft",
    prompt:
      "Identify the four aircraft classes shown and state what each gives way to.",
    fields: Array.from(
      { length: 4 },
      (_, index) => [
        field(
          `class${index + 1}name`,
          `Aircraft ${index + 1} – name`,
          { multiline: false }
        ),
        field(
          `class${index + 1}givesway`,
          `Aircraft ${index + 1} – gives way to`
        ),
      ]
    ).flat(),
  },
  {
    id: "airmanship-10",
    section:
      "airmanship",
    code: "P5",
    type: "know",
    mandatory: true,
    page: 12,
    title:
      "Rules of the Air",
    prompt:
      "Describe what similar aircraft should do when overtaking, approaching head-on, converging, approaching to land and when more than one aircraft is landing or an emergency exists.",
    fields: [
      field(
        "overtaking",
        "Overtaking"
      ),
      field(
        "headon",
        "Approaching head on"
      ),
      field(
        "converging",
        "Converging"
      ),
      field(
        "landing",
        "Approaching to land"
      ),
      field(
        "multiple",
        "More than one aircraft coming into land / emergency landings"
      ),
    ],
  },
  {
    id: "airmanship-11",
    section:
      "airmanship",
    code: "P6",
    type: "know",
    mandatory: true,
    page: 13,
    title:
      "Navigation Lights",
    prompt:
      "Describe the colour and location of navigation lights for each aircraft class shown.",
    fields: [
      field(
        "balloons",
        "Balloons"
      ),
      field(
        "gliders",
        "Gliders"
      ),
      field(
        "airships",
        "Airships"
      ),
      field(
        "conventional",
        "Conventional Aircraft"
      ),
    ],
  },
  {
    id: "airmanship-12",
    section:
      "airmanship",
    code: "P7",
    type: "know",
    mandatory: true,
    page: 14,
    title:
      "The Clock Code",
    prompt:
      "Describe the clock-code reporting method using the diagrams in the source notebook.",
    fields: [
      field(
        "answer",
        "The Clock Code Reporting Method",
        { rows: 8 }
      ),
    ],
  },
  {
    id: "airmanship-13",
    section:
      "airmanship",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 15,
    title:
      "Different Rights of Way",
    prompt:
      "Explain why each class has a different right of way. Consider manoeuvrability.",
    fields: [
      field(
        "balloons",
        "Balloons"
      ),
      field(
        "gliders",
        "Gliders"
      ),
      field(
        "airships",
        "Airships"
      ),
      field(
        "powered",
        "Powered Aircraft"
      ),
    ],
  },
  {
    id: "airmanship-14",
    section:
      "airmanship",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 15,
    title:
      "Importance of Navigation Lights",
    prompt:
      "Explain why navigation lights are important, when they should be used and why their positions differ by aircraft type.",
    fields: [
      field(
        "answer",
        "Your answer",
        { rows: 9 }
      ),
    ],
  },
  {
    id: "airmanship-15",
    section:
      "airmanship",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 16,
    title:
      "Importance of Following the Rules of the Air",
    prompt:
      "Explain why each reason is important and the consequences of ignoring it.",
    fields: [
      field(
        "safe",
        "To allow aircraft to operate safely"
      ),
      field(
        "standardisation",
        "To promote standardisation"
      ),
      field(
        "congestion",
        "To prevent congestion and delays"
      ),
    ],
  },
  {
    id: "airmanship-16",
    section:
      "airmanship",
    code: "P8",
    type: "know",
    mandatory: true,
    page: 17,
    title:
      "Military Aircraft Designations",
    prompt:
      "Give the meaning of each military aircraft suffix listed in the source notebook.",
    fields: [
      ..."AEW,B,C,E,F,FG,GR,HAR,HAS,T,HC,HCC,HT,HU,K,MR,R,PR,S"
        .split(",")
        .map(
          (suffix) =>
            field(
              `suffix${suffix}`,
              suffix,
              { multiline: false }
            )
        ),
    ],
  },

  // =======================================================================
  // SECTION 2 — PRINCIPLES OF FLIGHT
  // =======================================================================
  {
    id: "flight-1",
    section: "flight",
    code: "P1",
    type: "know",
    mandatory: true,
    page: 20,
    title:
      "Creation of Lift in Flight",
    prompt:
      "Describe how each of the six listed factors affects lift.",
    fields: groupedFields([
      {
        key: "factor",
        label: "Factor",
        items: [
          "Downwash",
          "Airspeed",
          "Air Density",
          "Angle of Attack",
          "Shape / Area of a Wing",
          "Flaps",
        ],
      },
    ]),
  },
  {
    id: "flight-2",
    section: "flight",
    code: "P2",
    type: "know",
    mandatory: true,
    page: 21,
    title:
      "Thrust, Drag, Weight and Lift",
    prompt:
      "Describe how lift and weight, and thrust and drag, affect an aircraft.",
    fields: [
      field(
        "liftWeight",
        "Lift and Weight",
        { rows: 6 }
      ),
      field(
        "thrustDrag",
        "Thrust and Drag",
        { rows: 6 }
      ),
    ],
  },
  {
    id: "flight-3",
    section: "flight",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 22,
    title: "Swing Wings",
    prompt:
      "Explain how aircraft performance changes with the wings forward and with the wings swept.",
    fields: [
      field(
        "forward",
        "Wings Forward",
        { rows: 6 }
      ),
      field(
        "swept",
        "Wings Swept",
        { rows: 6 }
      ),
    ],
  },
  {
    id: "flight-4",
    section: "flight",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 23,
    title:
      "Changes in Forces During Flight",
    prompt:
      "Describe how lift, weight, thrust and drag can change during a flight.",
    fields: [
      field("lift", "Lift"),
      field("weight", "Weight"),
      field("thrust", "Thrust"),
      field("drag", "Drag"),
    ],
  },
  {
    id: "flight-5",
    section: "flight",
    code: "P3",
    type: "know",
    mandatory: true,
    page: 24,
    title:
      "Stability of an Aeroplane",
    prompt:
      "Explain how an aeroplane is kept stable in flight and distinguish static from dynamic stability.",
    fields: [
      field(
        "static",
        "What is Static Stability?",
        { rows: 7 }
      ),
      field(
        "dynamic",
        "What is Dynamic Stability?",
        { rows: 7 }
      ),
    ],
  },
  {
    id: "flight-6",
    section: "flight",
    code: "P4",
    type: "know",
    mandatory: true,
    page: 25,
    title: "Stalls",
    prompt:
      "Describe why each factor might cause an aircraft to stall.",
    fields: groupedFields([
      {
        key: "stall",
        label: "Factor",
        items: [
          "Weight",
          "Ice and Dirt",
          "Wing Damage",
          "Power",
          "Flaps",
          "Turning the Aircraft",
        ],
      },
    ]),
  },
  {
    id: "flight-7",
    section: "flight",
    code: "P5",
    type: "know",
    mandatory: true,
    page: 26,
    title:
      "Aeroplane Manoeuvrability",
    prompt:
      "Explain how rudder, elevators and ailerons make an aircraft manoeuvrable.",
    fields: [
      field("rudder", "Rudder"),
      field("elevators", "Elevators"),
      field("ailerons", "Ailerons"),
    ],
  },
  {
    id: "flight-8",
    section: "flight",
    code: "P6",
    type: "know",
    mandatory: true,
    page: 27,
    title:
      "Controls Used for Manoeuvrability",
    prompt:
      "Describe the control movement and aircraft response for pitch, roll and yaw.",
    fields: [
      field("pitch", "Pitch"),
      field("roll", "Roll"),
      field("yaw", "Yaw"),
    ],
  },
  {
    id: "flight-9",
    section: "flight",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 28,
    title:
      "Wing Features and Landing",
    prompt:
      "Describe how flaps, slats/slots and air brakes assist an aircraft during landing.",
    fields: [
      field("flaps", "Flaps"),
      field("slats", "Slats & Slots"),
      field("airbrakes", "Air Brakes"),
    ],
  },
  {
    id: "flight-10",
    section: "flight",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 29,
    title:
      "Manoeuvring at Low Airspeed",
    prompt:
      "Analyse the effects on the aircraft when manoeuvring at critically low airspeed. Consider lift, stalling and handling.",
    fields: [
      field(
        "answer",
        "Your analysis",
        { rows: 10 }
      ),
    ],
  },
  {
    id: "flight-11",
    section: "flight",
    code: "P7",
    type: "know",
    mandatory: true,
    page: 30,
    title:
      "Forces on a Glider in Flight",
    prompt:
      "Identify Force 1, Force 2, Force 3 and the line shown, then describe the difference between the forces acting on a glider and powered aircraft.",
    fields: [
      field("force1", "Force 1", { multiline: false }),
      field("force2", "Force 2", { multiline: false }),
      field("force3", "Force 3", { multiline: false }),
      field("line", "Line", { multiline: false }),
      field(
        "difference",
        "Difference Between Powered Aircraft and Gliders",
        { rows: 6 }
      ),
    ],
  },
  {
    id: "flight-12",
    section: "flight",
    code: "P8",
    type: "know",
    mandatory: true,
    page: 31,
    title:
      "Helicopter Controls",
    prompt:
      "List the three main helicopter controls, the control surface they affect and the helicopter's resulting action.",
    fields: Array.from(
      { length: 3 },
      (_, index) => [
        field(
          `control${index + 1}`,
          `Control ${index + 1}`,
          { multiline: false }
        ),
        field(
          `surface${index + 1}`,
          `Control Surface ${index + 1}`,
          { multiline: false }
        ),
        field(
          `action${index + 1}`,
          `Helicopter's Action ${index + 1}`
        ),
      ]
    ).flat(),
  },
  {
    id: "flight-13",
    section: "flight",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 31,
    title:
      "Helicopter and Fixed-Wing Yaw",
    prompt:
      "Compare the use of a helicopter tail rotor and a fixed-wing rudder to manoeuvre in yaw.",
    fields: [
      field(
        "similarities",
        "Similarities",
        { rows: 7 }
      ),
      field(
        "differences",
        "Differences",
        { rows: 7 }
      ),
    ],
  },

  // =======================================================================
  // SECTION 3 — BASIC NAVIGATION USING MAP AND COMPASS
  // =======================================================================
  {
    id: "navigation-1",
    section: "navigation",
    code: "P1",
    type: "know",
    mandatory: true,
    page: 34,
    title: "Types of North",
    prompt:
      "Name the three types of north and give two facts about each.",
    fields: Array.from(
      { length: 3 },
      (_, index) => [
        field(
          `north${index + 1}name`,
          `Type of North ${index + 1}`,
          { multiline: false }
        ),
        field(
          `north${index + 1}fact1`,
          `Type ${index + 1} – Fact 1`
        ),
        field(
          `north${index + 1}fact2`,
          `Type ${index + 1} – Fact 2`
        ),
      ]
    ).flat(),
  },
  {
    id: "navigation-2",
    section: "navigation",
    code: "P2",
    type: "know",
    mandatory: true,
    page: 35,
    title:
      "Finding North Without a Compass",
    prompt:
      "Explain the four methods shown for finding north without a compass.",
    fields: [
      field(
        "map",
        "Finding Grid North with a map"
      ),
      field(
        "stars",
        "Finding True North by the stars"
      ),
      field(
        "watch",
        "Finding True North with a watch"
      ),
      field(
        "shadows",
        "Finding True North using shadows"
      ),
    ],
  },
  {
    id: "navigation-3",
    section: "navigation",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 36,
    title:
      "Variances in Types of North",
    prompt:
      "For each type of north, give a description and explain how it varies from the others.",
    fields: Array.from(
      { length: 3 },
      (_, index) => [
        field(
          `type${index + 1}`,
          `Type of North ${index + 1}`,
          { multiline: false }
        ),
        field(
          `description${index + 1}`,
          `Description ${index + 1}`
        ),
        field(
          `variance${index + 1}`,
          `Variance ${index + 1}`
        ),
      ]
    ).flat(),
  },
  {
    id: "navigation-4",
    section: "navigation",
    code: "P3",
    type: "know",
    mandatory: true,
    page: 37,
    title:
      "Parts of a Compass",
    prompt:
      "Label parts 1 to 8 of the lightweight walking compass shown.",
    fields:
      listFields(
        "part",
        8,
        "Compass part"
      ),
  },
  {
    id: "navigation-5",
    section: "navigation",
    code: "P4",
    type: "know",
    mandatory: true,
    page: 37,
    title:
      "Magnetic and Grid Bearing Calculations",
    prompt:
      "Complete the six bearing calculations shown in the notebook. Use the answer field for each row.",
    fields:
      listFields(
        "calculation",
        6,
        "Answer"
      ),
  },
  {
    id: "navigation-6",
    section: "navigation",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 38,
    title:
      "Setting a Map, Headings and Bearings",
    prompt:
      "Explain how to set a map, set a compass heading and walk on a bearing.",
    fields: [
      field(
        "map",
        "Setting the map",
        { rows: 6 }
      ),
      field(
        "heading",
        "Setting a compass heading",
        { rows: 6 }
      ),
      field(
        "bearing",
        "Walking on a bearing",
        { rows: 6 }
      ),
    ],
  },
  {
    id: "navigation-7",
    section: "navigation",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 39,
    title:
      "Justifying Your Position on a Map",
    prompt:
      "Use the two map scenarios in the source notebook to justify whether the stated route/bearing is correct.",
    fields: [
      field(
        "scenario1",
        "Scenario 1 – Explanation",
        { rows: 7 }
      ),
      field(
        "scenario2",
        "Scenario 2 – Explanation",
        { rows: 7 }
      ),
    ],
  },
  {
    id: "navigation-8",
    section: "navigation",
    code: "P5",
    type: "know",
    mandatory: true,
    page: 40,
    title: "Naismith's Rule",
    prompt:
      "Fill in the missing values for the nine rows in the Naismith's Rule table.",
    fields:
      listFields(
        "naismith",
        9,
        "Row"
      ),
  },
  {
    id: "navigation-9",
    section: "navigation",
    code: "P6",
    type: "know",
    mandatory: true,
    page: 41,
    title:
      "Navigation Techniques",
    prompt:
      "Define each of the four navigation techniques.",
    fields: [
      field(
        "handrailing",
        "Handrailing"
      ),
      field(
        "aimingOff",
        "Aiming Off"
      ),
      field(
        "contouring",
        "Contouring"
      ),
      field(
        "attackPoints",
        "Attack Points"
      ),
    ],
  },
  {
    id: "navigation-10",
    section: "navigation",
    code: "P7",
    type: "know",
    mandatory: true,
    page: 42,
    title:
      "Atmospheric Pressure",
    prompt:
      "Describe typical weather associated with low and high pressure and explain how that weather can affect navigation.",
    fields: [
      field(
        "lowWeather",
        "Depression (Low Pressure) – Typical Weather"
      ),
      field(
        "lowNavigation",
        "How could low-pressure weather affect your navigation?"
      ),
      field(
        "highWeather",
        "Anticyclone (High Pressure) – Typical Weather"
      ),
      field(
        "highNavigation",
        "How could high-pressure weather affect your navigation?"
      ),
    ],
  },
  {
    id: "navigation-11",
    section: "navigation",
    code: "P8",
    type: "know",
    mandatory: true,
    page: 43,
    referencePages: [
      43,
      44,
    ],
    title: "Types of Cloud",
    prompt:
      "Describe each cloud type and the weather it produces.",
    fields: [
      ..."Cumulonimbus,Cumulus,Altocumulus,Nimbostratus,Altostratus,Cirrostratus,Cirrus"
        .split(",")
        .map(
          (cloud) =>
            field(
              `cloud${cloud}`,
              `${cloud} Clouds`
            )
        ),
    ],
  },
  {
    id: "navigation-12",
    section: "navigation",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 44,
    title:
      "Weather Fronts",
    prompt:
      "Name the three front types shown and describe how each is formed and the weather it typically produces.",
    fields: Array.from(
      { length: 3 },
      (_, index) => [
        field(
          `front${index + 1}name`,
          `Front ${index + 1} – Name`,
          { multiline: false }
        ),
        field(
          `front${index + 1}description`,
          `Front ${index + 1} – Description`
        ),
      ]
    ).flat(),
  },
  {
    id: "navigation-13",
    section: "navigation",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 45,
    title: "Depressions",
    prompt:
      "Use the weather chart to identify the weather system and describe the weather likely on the hill as a depression passes over.",
    fields: [
      field(
        "system",
        "What weather system is shown?",
        { multiline: false }
      ),
      field(
        "weather",
        "What weather would you experience on the hill?",
        { rows: 7 }
      ),
    ],
  },
  {
    id: "navigation-14",
    section: "navigation",
    code: "Develop",
    type: "develop",
    mandatory: false,
    page: 45,
    title:
      "Planning for Forecast Depressions",
    prompt:
      "List likely weather/conditions, what each would mean for the walk and sensible precautions.",
    fields: Array.from(
      { length: 3 },
      (_, index) => [
        field(
          `weather${index + 1}`,
          `Row ${index + 1} – Weather`
        ),
        field(
          `effect${index + 1}`,
          `Row ${index + 1} – What this means for your walk`
        ),
        field(
          `precaution${index + 1}`,
          `Row ${index + 1} – Precautions`
        ),
      ]
    ).flat(),
  },
];

const TYPE_STYLES = {
  know: {
    label:
      "Essential Knowledge – mandatory",
    chip:
      "bg-slate-100 text-raf-navy border-slate-300",
    banner:
      "bg-raf-navy text-white",
  },
  develop: {
    label:
      "Develop Understanding – optional",
    chip:
      "bg-blue-100 text-blue-800 border-blue-200",
    banner:
      "bg-raf-blue text-white",
  },
};

function SourcePage({
  task,
}) {
  const [open, setOpen] =
    useState(false);

  const pages =
    task.referencePages ||
    [task.page];

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
      <button
        type="button"
        onClick={() =>
          setOpen(
            (previous) =>
              !previous
          )
        }
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-raf-navy" />

          <div>
            <p className="font-bold text-raf-navy">
              Original notebook
              reference
            </p>

            <p className="text-xs text-slate-500 mt-0.5">
              Page
              {pages.length > 1
                ? "s"
                : ""}{" "}
              {pages.join(", ")}
            </p>
          </div>
        </div>

        <ChevronDown
          className={[
            "w-5 h-5 text-slate-500 transition-transform",
            open
              ? "rotate-180"
              : "",
          ].join(" ")}
        />
      </button>

      {open && (
        <div className="border-t border-slate-200 bg-white">
          <div className="p-3 flex flex-wrap gap-2">
            {pages.map(
              (page) => (
                <a
                  key={page}
                  href={`${PDF_PATH}#page=${page}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-raf-navy"
                >
                  <ExternalLink
                    size={15}
                  />
                  Open page{" "}
                  {page}
                </a>
              )
            )}

            <a
              href={PDF_PATH}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-raf-navy"
            >
              <BookOpen
                size={15}
              />
              Full notebook
            </a>
          </div>

          <div className="hidden md:block border-t border-slate-200">
            <iframe
              title={`Leading source page ${pages[0]}`}
              src={`${PDF_PATH}#page=${pages[0]}&zoom=page-width`}
              className="w-full h-[650px]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function AnswerField({
  definition,
  value,
  onChange,
}) {
  const common =
    "mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-raf-blue focus:ring-2 focus:ring-blue-100";

  return (
    <label className="block">
      <span className="text-sm font-bold text-raf-navy">
        {definition.label}
      </span>

      {definition.multiline ? (
        <textarea
          rows={
            definition.rows
          }
          value={
            value || ""
          }
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          placeholder={
            definition.placeholder
          }
          className={`${common} resize-y`}
        />
      ) : (
        <input
          type="text"
          value={
            value || ""
          }
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          placeholder={
            definition.placeholder
          }
          className={common}
        />
      )}
    </label>
  );
}

function StatusChip({
  task,
  submittedAt,
  approved,
  review,
}) {
  const submitted =
    Boolean(submittedAt);

  const assessedAt =
    review?.assessed_at
      ? new Date(
          review.assessed_at
        ).getTime()
      : 0;

  const submittedTime =
    submittedAt
      ? new Date(
          submittedAt
        ).getTime()
      : 0;

  const returned =
    review?.review_outcome ===
      "returned" &&
    submittedTime <=
      assessedAt;

  if (approved) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">
        <CheckCircle2
          size={13}
        />
        Staff approved
      </span>
    );
  }

  if (returned) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-100 px-2.5 py-1 text-xs font-bold text-red-800">
        <AlertTriangle
          size={13}
        />
        Returned for
        changes
      </span>
    );
  }

  if (submitted) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900">
        <ClipboardCheck
          size={13}
        />
        Awaiting staff
        check
      </span>
    );
  }

  const style =
    TYPE_STYLES[
      task.type
    ];

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${style.chip}`}
    >
      {style.label}
    </span>
  );
}

function TaskCard({
  task,
  answers,
  submittedAt,
  approved,
  review,
  updateAnswer,
  submitTask,
  submitting,
  error,
}) {
  const style =
    TYPE_STYLES[
      task.type
    ];

  const submitted =
    Boolean(submittedAt);

  const assessedAt =
    review?.assessed_at
      ? new Date(
          review.assessed_at
        ).getTime()
      : 0;

  const submittedTime =
    submittedAt
      ? new Date(
          submittedAt
        ).getTime()
      : 0;

  const returned =
    review?.review_outcome ===
      "returned" &&
    submittedTime <=
      assessedAt;

  const resubmitted =
    review?.review_outcome ===
      "returned" &&
    submittedTime >
      assessedAt;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div
        className={`px-5 py-3 font-black text-sm uppercase tracking-wider ${style.banner}`}
      >
        {style.label}
      </div>

      <div className="p-5 md:p-7">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              {task.code}
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-raf-navy mt-1">
              {task.title}
            </h2>

            <p className="text-slate-600 leading-relaxed mt-3">
              {task.prompt}
            </p>
          </div>

          <StatusChip
            task={task}
            submittedAt={
              submittedAt
            }
            approved={
              approved
            }
            review={review}
          />
        </div>

        <div className="mt-5">
          <SourcePage
            task={task}
          />
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {task.fields.map(
            (definition) => (
              <AnswerField
                key={
                  definition.key
                }
                definition={
                  definition
                }
                value={
                  answers?.[
                    definition.key
                  ]
                }
                onChange={(
                  value
                ) =>
                  updateAnswer(
                    task.id,
                    definition.key,
                    value
                  )
                }
              />
            )
          )}
        </div>

        {review?.review_outcome && (
          <div
            className={[
              "mt-6 rounded-xl border p-4",
              approved
                ? "border-emerald-200 bg-emerald-50"
                : returned
                  ? "border-red-200 bg-red-50"
                  : "border-blue-200 bg-blue-50",
            ].join(" ")}
          >
            <div className="flex items-start gap-3">
              {approved ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
              ) : (
                <ClipboardCheck className="w-6 h-6 text-raf-blue shrink-0" />
              )}

              <div>
                <p className="font-black text-raf-navy">
                  {approved
                    ? "Instructor assessment – approved"
                    : returned
                      ? "Instructor assessment – returned for changes"
                      : "Previous instructor feedback"}
                </p>

                <p className="text-sm text-slate-700 mt-1">
                  {
                    review.assessor_rank
                  }{" "}
                  {
                    review.assessor_name
                  }
                  {review.assessed_at
                    ? ` • ${new Intl.DateTimeFormat(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      ).format(
                        new Date(
                          review.assessed_at
                        )
                      )}`
                    : ""}
                </p>

                {review.review_feedback && (
                  <div className="mt-3 rounded-lg bg-white/70 border border-black/5 p-3">
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-500">
                      Feedback
                    </p>

                    <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">
                      {
                        review.review_feedback
                      }
                    </p>
                  </div>
                )}

                {resubmitted && (
                  <p className="text-sm font-semibold text-blue-900 mt-3">
                    You have
                    resubmitted this
                    activity after the
                    feedback above. It
                    is awaiting
                    another staff
                    check.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />

            <p className="text-sm text-red-800">
              {error}
            </p>
          </div>
        )}

        <div className="mt-7 border-t border-slate-200 pt-5">
          {approved ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
              <FileCheck2 className="w-6 h-6 text-emerald-700 shrink-0" />

              <div>
                <p className="font-bold text-emerald-900">
                  Activity approved
                </p>

                <p className="text-sm text-emerald-800 mt-1">
                  An instructor has
                  marked this activity
                  satisfactory.
                </p>
              </div>
            </div>
          ) : returned ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="font-bold text-red-900">
                  Changes requested
                </p>

                <p className="text-sm text-red-800 mt-1">
                  Read the instructor
                  feedback above,
                  amend your work and
                  resubmit it.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  submitTask(task)
                }
                disabled={
                  submitting
                }
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-raf-red px-5 py-3 font-black text-white disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2
                    size={18}
                    className="animate-spin"
                  />
                ) : (
                  <ClipboardCheck
                    size={18}
                  />
                )}

                Resubmit for Staff
                Check
              </button>
            </div>
          ) : submitted ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <LockKeyhole className="w-6 h-6 text-amber-700 shrink-0" />

              <div>
                <p className="font-bold text-amber-950">
                  Ready for staff
                  check
                </p>

                <p className="text-sm text-amber-900 mt-1">
                  Your work is saved
                  and waiting for an
                  instructor.
                </p>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() =>
                submitTask(task)
              }
              disabled={
                submitting
              }
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-raf-red px-5 py-3 font-black text-white disabled:opacity-50"
            >
              {submitting ? (
                <Loader2
                  size={18}
                  className="animate-spin"
                />
              ) : (
                <ClipboardCheck
                  size={18}
                />
              )}

              Ready for Staff Check
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Leading() {
  const navigate =
    useNavigate();

  const {
    active,
    serviceNumber,
    activity,
    activityProgress,
    currentStep,
    sessionData,
    setCurrentStep,
    setSessionData,
    queueSave,
    saveState,
    updateModule,
    endSession,
    saving,
    ending,
    error,
  } = useTrainingSession();

  const [selectedSection, setSelectedSection] =
    useState(
      sessionData?.leadingSection ||
        "airmanship"
    );

  const [selectedTaskId, setSelectedTaskId] =
    useState(
      LEADING_TASKS.some(
        (task) =>
          task.id ===
          currentStep
      )
        ? currentStep
        : LEADING_TASKS[0].id
    );

  const [showOptional, setShowOptional] =
    useState(
      Boolean(
        sessionData?.leadingShowOptional
      )
    );

  const [localError, setLocalError] =
    useState("");

  const [submittingTask, setSubmittingTask] =
    useState("");

  const answers =
    sessionData?.leadingAnswers ||
    {};

  const submittedTasks =
    sessionData?.leadingSubmitted ||
    {};

  const modules =
    useMemo(() => {
      const result = {};

      for (const module of
        activityProgress?.modules ||
        []) {
        result[
          module.module_id
        ] = module;
      }

      return result;
    }, [activityProgress]);

  const approvedTasks =
    useMemo(() => {
      const result = {};

      Object.entries(
        modules
      ).forEach(
        ([key, module]) => {
          if (
            module?.status ===
              "complete" ||
            module?.review_outcome ===
              "approved"
          ) {
            result[key] =
              true;
          }
        }
      );

      return result;
    }, [modules]);

  useEffect(() => {
    if (
      !active ||
      !serviceNumber ||
      activity !== "leading"
    ) {
      navigate(
        "/training",
        { replace: true }
      );
    }
  }, [
    active,
    activity,
    navigate,
    serviceNumber,
  ]);

  useEffect(() => {
    if (
      currentStep &&
      LEADING_TASKS.some(
        (task) =>
          task.id ===
          currentStep
      )
    ) {
      const task =
        LEADING_TASKS.find(
          (item) =>
            item.id ===
            currentStep
        );

      setSelectedTaskId(
        currentStep
      );

      if (task?.section) {
        setSelectedSection(
          task.section
        );
      }
    }
  }, [currentStep]);

  const updateSession =
    useCallback(
      (updater) => {
        setSessionData(
          (previous) =>
            updater(
              previous || {}
            )
        );

        queueSave();
      },
      [
        queueSave,
        setSessionData,
      ]
    );

  const updateAnswer =
    useCallback(
      (
        taskId,
        key,
        value
      ) => {
        updateSession(
          (previous) => ({
            ...previous,
            leadingAnswers: {
              ...(
                previous.leadingAnswers ||
                {}
              ),
              [taskId]: {
                ...(
                  previous.leadingAnswers?.[
                    taskId
                  ] || {}
                ),
                [key]: value,
              },
            },
          })
        );
      },
      [updateSession]
    );

  const openTask =
    useCallback(
      (task) => {
        setSelectedTaskId(
          task.id
        );

        setSelectedSection(
          task.section
        );

        setCurrentStep(
          task.id
        );

        setLocalError("");

        updateSession(
          (previous) => ({
            ...previous,
            leadingSection:
              task.section,
          })
        );

        if (
          !approvedTasks[
            task.id
          ]
        ) {
          updateModule(
            task.id,
            "in_progress"
          ).catch(() => {});
        }
      },
      [
        approvedTasks,
        setCurrentStep,
        updateModule,
        updateSession,
      ]
    );

  const validateTask =
    useCallback(
      (task) => {
        const taskAnswers =
          answers[
            task.id
          ] || {};

        const missing =
          task.fields.find(
            (definition) =>
              !String(
                taskAnswers[
                  definition.key
                ] || ""
              ).trim()
          );

        if (missing) {
          return `Complete "${missing.label}" before submitting this activity.`;
        }

        return "";
      },
      [answers]
    );

  const submitTask =
    useCallback(
      async (task) => {
        setLocalError("");

        const validation =
          validateTask(task);

        if (validation) {
          setLocalError(
            validation
          );

          return;
        }

        setSubmittingTask(
          task.id
        );

        try {
          const nextSubmitted = {
            ...submittedTasks,
            [task.id]:
              new Date().toISOString(),
          };

          const nextData = {
            ...sessionData,
            leadingSubmitted:
              nextSubmitted,
            leadingSection:
              task.section,
          };

          setSessionData(
            nextData
          );

          await saveState(
            task.id,
            nextData
          );

          await updateModule(
            task.id,
            "in_progress"
          );
        } catch (err) {
          setLocalError(
            err?.response?.data?.detail ||
              err?.message ||
              "Unable to submit this activity."
          );
        } finally {
          setSubmittingTask("");
        }
      },
      [
        saveState,
        sessionData,
        setSessionData,
        submittedTasks,
        updateModule,
        validateTask,
      ]
    );

  const filteredTasks =
    useMemo(
      () =>
        LEADING_TASKS.filter(
          (task) =>
            task.section ===
              selectedSection &&
            (showOptional ||
              task.mandatory)
        ),
      [
        selectedSection,
        showOptional,
      ]
    );

  useEffect(() => {
    if (
      !filteredTasks.some(
        (task) =>
          task.id ===
          selectedTaskId
      )
    ) {
      const next =
        filteredTasks[0];

      if (next) {
        setSelectedTaskId(
          next.id
        );
      }
    }
  }, [
    filteredTasks,
    selectedTaskId,
  ]);

  const selectedTask =
    LEADING_TASKS.find(
      (task) =>
        task.id ===
        selectedTaskId
    ) ||
    filteredTasks[0];

  const selectedIndex =
    filteredTasks.findIndex(
      (task) =>
        task.id ===
        selectedTask?.id
    );

  const mandatoryTasks =
    LEADING_TASKS.filter(
      (task) =>
        task.mandatory
    );

  const submittedMandatory =
    mandatoryTasks.filter(
      (task) =>
        submittedTasks[
          task.id
        ] ||
        approvedTasks[
          task.id
        ]
    ).length;

  const approvedMandatory =
    mandatoryTasks.filter(
      (task) =>
        approvedTasks[
          task.id
        ]
    ).length;

  const submissionPercent =
    mandatoryTasks.length
      ? Math.round(
          (submittedMandatory /
            mandatoryTasks.length) *
            100
        )
      : 0;

  const toggleOptional = () => {
    const next =
      !showOptional;

    setShowOptional(next);

    updateSession(
      (previous) => ({
        ...previous,
        leadingShowOptional:
          next,
      })
    );
  };

  const endAndSave =
    async () => {
      setLocalError("");

      try {
        await endSession();

        navigate(
          "/training",
          { replace: true }
        );
      } catch (err) {
        setLocalError(
          err?.response?.data?.detail ||
            err?.message ||
            "Unable to end the training session."
        );
      }
    };

  if (
    !active ||
    !serviceNumber
  ) {
    return (
      <div className="min-h-screen bg-raf-navy text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-raf-navy text-white border-b-4 border-raf-red">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                <Gauge size={23} />
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-white/60 font-bold">
                  Classification
                  Training
                </p>

                <h1 className="text-xl md:text-2xl font-black">
                  Leading Cadet
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="rounded-lg bg-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-widest text-white/50">
                  Training record
                </p>

                <p className="font-bold tracking-wide">
                  {formatServiceNumber(
                    serviceNumber
                  )}
                </p>
              </div>

              <button
                type="button"
                onClick={
                  endAndSave
                }
                disabled={
                  ending || saving
                }
                className="inline-flex items-center gap-2 rounded-lg bg-raf-red px-4 py-3 font-bold disabled:opacity-50"
              >
                {ending ? (
                  <Loader2
                    size={17}
                    className="animate-spin"
                  />
                ) : (
                  <Save
                    size={17}
                  />
                )}

                End Session &
                Save
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
        <div className="grid md:grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Essential Knowledge
            </p>

            <p className="text-2xl font-black text-raf-navy mt-1">
              {
                mandatoryTasks.length
              }
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Submitted
            </p>

            <p className="text-2xl font-black text-amber-700 mt-1">
              {
                submittedMandatory
              }
              {" / "}
              {
                mandatoryTasks.length
              }
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Staff Approved
            </p>

            <p className="text-2xl font-black text-emerald-700 mt-1">
              {
                approvedMandatory
              }
              {" / "}
              {
                mandatoryTasks.length
              }
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-bold text-raf-navy">
                Essential Knowledge
                submitted
              </p>

              <p className="text-sm text-slate-500 mt-1">
                Develop
                Understanding
                activities are
                optional.
              </p>
            </div>

            <p className="text-2xl font-black text-raf-navy">
              {
                submissionPercent
              }
              %
            </p>
          </div>

          <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-raf-blue transition-all duration-300"
              style={{
                width: `${submissionPercent}%`,
              }}
            />
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 flex gap-3">
          <ShieldCheck className="w-5 h-5 text-blue-800 shrink-0 mt-0.5" />

          <div className="text-sm text-blue-950">
            <p className="font-bold">
              Notebook completion
              supports the Leading
              Cadet assessments.
            </p>

            <p className="mt-1">
              The source notebook
              says Essential
              Knowledge activities
              provide the knowledge
              needed for the online
              assessment, while
              Develop Understanding
              tasks are optional.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-5">
          <aside className="lg:sticky lg:top-4 lg:self-start space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
                  Leading subjects
                </p>
              </div>

              <div className="p-2">
                {LEADING_SECTIONS.map(
                  (section) => {
                    const Icon =
                      section.icon;

                    const activeSection =
                      selectedSection ===
                      section.id;

                    return (
                      <button
                        key={
                          section.id
                        }
                        type="button"
                        onClick={() => {
                          setSelectedSection(
                            section.id
                          );

                          const next =
                            LEADING_TASKS.find(
                              (task) =>
                                task.section ===
                                  section.id &&
                                (showOptional ||
                                  task.mandatory)
                            );

                          if (next) {
                            openTask(
                              next
                            );
                          }
                        }}
                        className={[
                          "w-full rounded-lg p-3 text-left transition flex items-center gap-3",
                          activeSection
                            ? "bg-raf-navy text-white"
                            : "hover:bg-slate-50 text-slate-700",
                        ].join(" ")}
                      >
                        <Icon
                          size={19}
                        />

                        <span className="font-semibold leading-tight">
                          {
                            section.title
                          }
                        </span>
                      </button>
                    );
                  }
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={
                toggleOptional
              }
              className={[
                "w-full rounded-xl border p-4 text-left transition",
                showOptional
                  ? "border-blue-300 bg-blue-50"
                  : "border-slate-200 bg-white",
              ].join(" ")}
            >
              <p className="font-bold text-raf-navy">
                Develop
                Understanding
              </p>

              <p className="text-sm text-slate-500 mt-1">
                {showOptional
                  ? "Optional activities are shown."
                  : "Showing Essential Knowledge only."}
              </p>
            </button>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
                  Activities
                </p>
              </div>

              <div className="max-h-[56vh] overflow-y-auto p-2">
                {filteredTasks.map(
                  (task) => {
                    const selected =
                      task.id ===
                      selectedTask?.id;

                    const module =
                      modules[
                        task.id
                      ];

                    const returned =
                      module?.review_outcome ===
                      "returned";

                    return (
                      <button
                        key={
                          task.id
                        }
                        type="button"
                        onClick={() =>
                          openTask(
                            task
                          )
                        }
                        className={[
                          "w-full rounded-lg p-3 text-left transition mb-1",
                          selected
                            ? "bg-blue-50 border border-blue-200"
                            : "border border-transparent hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <div className="flex items-start gap-2">
                          {approvedTasks[
                            task.id
                          ] ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          ) : returned ? (
                            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                          ) : submittedTasks[
                              task.id
                            ] ? (
                            <ClipboardCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                          )}

                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                              {
                                task.code
                              }
                            </p>

                            <p className="text-sm font-semibold text-raf-navy leading-tight">
                              {
                                task.title
                              }
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          </aside>

          <main className="min-w-0">
            {selectedTask && (
              <TaskCard
                task={
                  selectedTask
                }
                answers={
                  answers[
                    selectedTask.id
                  ] || {}
                }
                submittedAt={
                  submittedTasks[
                    selectedTask.id
                  ] || ""
                }
                approved={Boolean(
                  approvedTasks[
                    selectedTask.id
                  ]
                )}
                review={
                  modules[
                    selectedTask.id
                  ] || null
                }
                updateAnswer={
                  updateAnswer
                }
                submitTask={
                  submitTask
                }
                submitting={
                  submittingTask ===
                  selectedTask.id
                }
                error={
                  localError ||
                  error
                }
              />
            )}

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                disabled={
                  selectedIndex <=
                  0
                }
                onClick={() => {
                  if (
                    selectedIndex >
                    0
                  ) {
                    openTask(
                      filteredTasks[
                        selectedIndex -
                          1
                      ]
                    );
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 font-bold text-raf-navy disabled:opacity-40"
              >
                <ChevronLeft
                  size={18}
                />
                Previous
              </button>

              <button
                type="button"
                disabled={
                  selectedIndex <
                    0 ||
                  selectedIndex >=
                    filteredTasks.length -
                      1
                }
                onClick={() => {
                  if (
                    selectedIndex >=
                      0 &&
                    selectedIndex <
                      filteredTasks.length -
                        1
                  ) {
                    openTask(
                      filteredTasks[
                        selectedIndex +
                          1
                      ]
                    );
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 font-bold text-raf-navy disabled:opacity-40"
              >
                Next
                <ChevronRight
                  size={18}
                />
              </button>
            </div>

            {submittedMandatory ===
              mandatoryTasks.length && (
              <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-5 flex gap-3">
                <CloudSun className="w-6 h-6 text-amber-700 shrink-0" />

                <div>
                  <h3 className="font-black text-amber-950">
                    All Essential
                    Knowledge work has
                    been submitted
                  </h3>

                  <p className="text-sm text-amber-900 mt-1">
                    Staff can now
                    review the
                    notebook. The
                    source notebook
                    states that, once
                    the Essential
                    Knowledge work is
                    complete, the
                    instructor may
                    register the
                    cadet for the
                    relevant Leading
                    Cadet exams.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-5">
              <button
                type="button"
                onClick={
                  endAndSave
                }
                disabled={
                  ending || saving
                }
                className="w-full bg-raf-red text-white rounded-xl py-4 px-6 font-black text-lg flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {ending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving
                    session...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    End Session &
                    Save
                  </>
                )}
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}